import { FC } from 'react';
import { useGetCurrentUserQuery } from '@teachur/http-client';
import { Checkbox } from '@new/common/ui/Checkbox/Checkbox';
import { Input } from '@new/common/ui/Input/Input';
import { Upload } from '@new/common/ui/Upload/Upload';
import { withForm } from '@new/common/hooks/form/form';
import { useUploadFile } from '@new/features/transcripts/shared/hooks/useUploadFile';
import { wizardDefaultValues } from '../../../schemas/wizard.schema';
import { Tooltip } from '@new/common/ui/Tooltip/Tooltip';

const ReadOnlyField: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-1 flex-col gap-2">
    <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
      {label}
    </label>
    <Input disabled value={value} onChange={() => {}} />
  </div>
);

export const PersonalInfoStep = withForm({
  defaultValues: wizardDefaultValues,
  render: function PersonalInfoStepRender({ form }) {
    const upload = useUploadFile();
    const { data: user } = useGetCurrentUserQuery();

    return (
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="m-0! [font-size:var(--fs-h5)]! font-bold text-(--neutral-black-NB1000)!">
            Personal Information
          </h2>
          <p className="mt-1 [font-size:var(--fs-p3)]! text-(--neutral-black-NB700)!">
            Have you earned any credits under a different name?
            <Tooltip
              content={
                <p className="m-0! text-(--neutral-black-NB1000)">
                  To accept transfer credits, Newlane must have documentation for any name on a
                  transcript that differs from the name on the government-issued ID you shared at
                  registration. If your transcript shows a different name, please provide
                  documentation of the name change.
                </p>
              }
            >
              <span className="text-(--danger-D700)!">*</span>
            </Tooltip>{' '}
            If not, just continue.
          </p>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <ReadOnlyField label="First name" value={user?.firstName ?? ''} />
          <ReadOnlyField label="Last name" value={user?.lastName ?? ''} />
        </div>

        <form.AppField name="hasNameChange">
          {(field) => (
            <label className="flex w-fit cursor-pointer items-center gap-3">
              <span className="[font-size:var(--fs-p3)]! text-(--neutral-black-NB1000)!">
                I have credits under a former / different name
              </span>
              <Checkbox value={!!field.state.value} onChange={(v) => field.handleChange(v)} />
            </label>
          )}
        </form.AppField>

        <form.Subscribe selector={(s) => s.values.hasNameChange}>
          {(hasNameChange) =>
            hasNameChange ? (
              <div className="flex flex-col gap-4">
                <form.AppField name="formerName">
                  {(field) => <field.Text label="Former name" placeholder="e.g. Jane Smith" />}
                </form.AppField>
                <form.AppField name="nameChangeReason">
                  {(field) => (
                    <field.Text label="Reason for name change" placeholder="e.g. Marriage" />
                  )}
                </form.AppField>
                <form.AppField name="nameChangeDocumentId">
                  {(field) => (
                    <div className="flex flex-col gap-2">
                      <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
                        Proof document (optional)
                      </label>
                      <Upload
                        accept=".pdf,.jpg,.jpeg,.png"
                        isUploading={upload.isLoading}
                        onUpload={async (file) => {
                          const id = await upload.uploadFile(file);
                          if (id != null) field.handleChange(id);
                        }}
                        onRemove={() => field.handleChange(null)}
                        supportedFormatsText="Supported formats: PDF, JPG, PNG"
                      />
                    </div>
                  )}
                </form.AppField>
              </div>
            ) : null
          }
        </form.Subscribe>
      </div>
    );
  },
});
